import { useAppDispatch } from '@redux/hooks';
import { fetchPreload } from '@redux/slices/preloadSlice';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const language = "japanese";
const baseURL = import.meta.env.VITE_API_URL;
const apiURL = baseURL + "/capi";

//{"success":true,"batchId":"wmvb4b251z","totalCards":2,"message":"Processing 2 cards. Check progress at /var/www/cards/progress.txt"}
interface UploadResponse {
  success: boolean;
  batchId?: string;
  totalCards?: number;
  message?: string;
  errors?: string[];
}

// {
//   "batchId": "wmvb4b251z",
//   "totalCards": 2,
//   "scannedCards": 2,
//   "savedCards": 2,
//   "currentCard": 2,
//   "status": "completed",
//   "errors": [],
//   "completedCardKeys": [
//     162,
//     163
//   ],
//   "startTime": "2025-07-08T04:48:55.085Z",
//   "lastUpdate": "2025-07-08T04:49:03.983Z"
// }
interface ProgressData {
  batchId: string;
  totalCards: number;
  scannedCards: number;
  savedCards: number;
  currentCard: number;
  status: string;
  errors: string[];
  completedCardKeys: number[];
  startTime: string;
  lastUpdate: string;
}

const BatchCardUpload: React.FC = () => {
  const dispatch = useAppDispatch();
  
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'uploading' | 'processing' | 'success' | 'error' | 'cancelled' | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const [progressError, setProgressError] = useState<string | null>(null);
  const cancelUploadRef = useRef(false);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const statusRef = useRef<{status: string | null, batchId: string | null}>({
    status: null,
    batchId: null
  });


  const pollingStartTime = useRef<number | null>(null);
    const lastProgressUpdate = useRef<number | null>(null);
  const currentInterval = useRef<number>(500);

  const STALL_TIMEOUT = 2 * 60 * 1000; // 2 minutes no progress

  // this function calculates dynamic timeout based on num of files
  const calculateTimeout = (fileCount: number) => {

    const timePerCard = 60 * 1000; // 1 min per card
    const baseTimeout = 5 * 60 * 1000; // 5 minu base
    return Math.min(baseTimeout + (fileCount * timePerCard), 60 * 60 * 1000); // max 1 hour
  };

  // update status ref when state changes
  useEffect(() => {
    statusRef.current = { status: uploadStatus, batchId };
  }, [uploadStatus, batchId]);

  // cleanup on unmount

useEffect(() => {
    return () => {
      // clear polling interval
    if (progressIntervalRef.current) {
        clearTimeout(progressIntervalRef.current);
        progressIntervalRef.current = null;

      }
      

      // cancel server operations
      const { status, batchId } = statusRef.current;
      if (status === 'uploading') {
        cancelUploadRef.current = true;
      } else if (status === 'processing' && batchId) {
        stopBatchProcess(batchId);
        
      }
    };
  }, []);

  const handlePollingTimeout = (reason: string) => {
    if (progressIntervalRef.current) {
      clearTimeout(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setProgressError(language === "japanese" ? `処理タイムアウト: ${reason}` : `Processing timeout: ${reason}`);
    setUploadStatus('error');
    setUploadError(language === "japanese" ? "処理に時間がかかりすぎました。もう一度お試しください。" : "Processing took too long. Please try again.");

  };

  const pollProgress = async (batchId: string) => {
    try {
      const response = await fetch(`${baseURL}/progress.txt`);
      if (!response.ok) {
        throw new Error('Progress unavailable');
      }
      const data: ProgressData = await response.json();
      
      if (data.batchId === batchId) {
        const currentProgressData = progressData;
        const hasProgressChanged = !currentProgressData || 
            currentProgressData.scannedCards !== data.scannedCards || 
            currentProgressData.savedCards !== data.savedCards;


        if (hasProgressChanged) {
          
          lastProgressUpdate.current = Date.now();
        }

        setProgressData(data);
        setProgressError(null);
        
        if (data.status === 'completed') {
          // short delay before showing success message so that user has time to see that everything finished
          setTimeout(() => {
            setUploadStatus('success');
          }, 1500);
          dispatch(fetchPreload()); // Refresh card data when processing completes
          if (progressIntervalRef.current) {
            clearTimeout(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
        }
      }
    } catch (error) {
      setProgressError('Progress unavailable. Please wait.');
    }
  };

  const startProgressPolling = (batchId: string) => {
    // reset timing
    pollingStartTime.current = Date.now();
    lastProgressUpdate.current = Date.now();
    currentInterval.current = 500;
    


    // clear existing interval
    if (progressIntervalRef.current) {
      clearTimeout(progressIntervalRef.current);
    }
    
  const poll = () => {
      const now = Date.now();
      const maxPollDuration = calculateTimeout(uploadFiles.length);
      
      // check max duration timeout
      if (now - pollingStartTime.current! > maxPollDuration) {
        handlePollingTimeout(language === "japanese" ? "最大ポーリング時間を超過しました" : "Maximum polling duration exceeded");
        return;
      }
      
      // check stall timeout
      if (now - lastProgressUpdate.current! > STALL_TIMEOUT) {
        handlePollingTimeout(language === "japanese" ? "進捗が検出されませんでした" : "No progress detected");
        return;
      }
      
      pollProgress(batchId);
      
      // schedule next poll, exponential backoff
      currentInterval.current = Math.min(currentInterval.current * 1.1, 5000);
      progressIntervalRef.current = setTimeout(poll, currentInterval.current);
    };
    
    poll();
  };

  const stopBatchProcess = async (batchId: string) => {
    try {
      const response = await fetch(`${apiURL}/stopBatch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          batchId,
          externalUserKey: "1",
          userToken: "7e16b5527c77ea58",
          companyKey: "2",
          companyApiKey: "09df728269"
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        return true;
      } else {
        console.error('Failed to stop batch process:', result.error);
        return false;
      }
    } catch (error) {
      console.error('Failed to stop batch process:', error);
      return false;
    }
  };

  const handleFileUpload = async (): Promise<UploadResponse> => {
    setIsUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(0);
    setUploadError(null);
    setProgressError(null);
    cancelUploadRef.current = false;
    
    try {
      // Create XMLHttpRequest for upload progress tracking
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      
      uploadFiles.forEach(file => {
        formData.append("files", file);
      });
      
      // Add required fields (these will be added by the axios interceptor, but we need them for raw XMLHttpRequest)
      formData.append("externalUserKey", "1");
      formData.append("userToken", "7e16b5527c77ea58");
      formData.append("companyKey", "2");
      formData.append("companyApiKey", "09df728269");
      
      return new Promise<UploadResponse>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            setUploadProgress(Math.round(percentComplete));
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            try {
              const response: UploadResponse = JSON.parse(xhr.responseText);
              resolve(response);
            } catch {
              reject(new Error(language === "japanese" ? "無効なレスポンス形式" : "Invalid response format"));
            }
          } else {
            reject(new Error(language === "japanese" ? `ステータス ${xhr.status} でアップロードが失敗しました` : `Upload failed with status ${xhr.status}`));
          }
        });
        
        xhr.addEventListener('error', () => {
          reject(new Error(language === "japanese" ? "ネットワークエラーが発生しました" : "Network error occurred"));
        });
        
        xhr.addEventListener('abort', () => {
          reject(new Error(language === "japanese" ? "プロセスがキャンセルされました。" : "Process has been cancelled."));
        });
        
        // Handle cancellation
        cancelUploadRef.current = false;
        const checkCancel = () => {
          if (cancelUploadRef.current) {
            xhr.abort();
          } else {
            setTimeout(checkCancel, 100);
          }
        };
        checkCancel();

        xhr.open('POST', `${apiURL}/batchCards`);
        xhr.send(formData);
      });
    } catch (error) {
      throw error;
    }
  };

  const handleStartUpload = async () => {
    setShowUploadModal(true);
    setUploadError(null);
    setProgressData(null);
    setBatchId(null);
    
    try {
      const response = await handleFileUpload();
      
      if (response.success && response.batchId) {
        setBatchId(response.batchId);
        setUploadStatus('processing');
        setUploadProgress(100); // Upload complete
        startProgressPolling(response.batchId);
      } else {
        throw new Error(response.message || 'Upload failed');
      }
    } catch (error) {
      setUploadStatus('error');
      setUploadError(error instanceof Error ? error.message : 'An error occurred during upload');
      setIsUploading(false);
      dispatch(fetchPreload()); // Refresh card data even if upload failed (in case partial data was saved)
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setUploadFiles(Array.from(files));
    }
  };

  const handleCancelUpload = async () => {
    // if upload phase, cancel the upload
    if (uploadStatus === 'uploading') {
      cancelUploadRef.current = true;
    } 
    // if processing phase (scanning/saving), stop the batch process
    else if (uploadStatus === 'processing' && batchId) {
      const stopped = await stopBatchProcess(batchId);
      if (stopped) {
        setUploadStatus('cancelled');
      }
    }
    
    //  clear progress polling
    if (progressIntervalRef.current) {
      clearTimeout(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    // Refresh card data when cancelled (in case partial processing occurred)
    dispatch(fetchPreload());
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setUploadStatus(null);
    setUploadProgress(0);
    setUploadError(null);
    setProgressError(null);
    setProgressData(null);
    setBatchId(null);
    cancelUploadRef.current = false;
    
    if (progressIntervalRef.current) {
      clearTimeout(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    // Clear file input
    const fileInput = document.getElementById('multi-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    setUploadFiles([]);
    setIsUploading(false);
  };

  const getScannedProgress = () => {
    if (!progressData) return 0;
    return Math.round((progressData.scannedCards / progressData.totalCards) * 100);
  };

  const getSavedProgress = () => {
    if (!progressData) return 0;
    return Math.round((progressData.savedCards / progressData.totalCards) * 100);
  };

  const uploadModal = showUploadModal && createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg p-6 w-full m-4 md:w-[30rem]">
        {(uploadStatus === 'uploading' || uploadStatus === 'processing') && (
          <>
            <h2 className="text-[#444] text-lg font-bold mb-4">
              {uploadStatus === 'uploading' 
                ? (language === "japanese" ? "画像をアップロード中..." : "Uploading Images...")
                : (language === "japanese" ? "画像を処理中..." : "Processing Images...")
              }
            </h2>
            
            {/* Upload Progress */}
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">{language === "japanese" ? "アップロード進捗" : "Upload Progress"}</p>
              <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                <div 
                  className={`h-full transition-all duration-300 ${uploadStatus === 'uploading' ? 'bg-blue-500' : 'bg-green-500'}`}
                  style={{ width: uploadStatus === 'uploading' ? `${uploadProgress}%` : '100%' }}
                />
              </div>
              <p className="text-center mt-1 text-gray-600">{uploadStatus === 'uploading' ? uploadProgress : 100}%</p>
            </div>

            {/* Scanning Progress */}
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                {progressData 
                  ? (language === "japanese" ? `スキャン進捗 (${progressData.scannedCards}/${progressData.totalCards})` : `Scanning Progress (${progressData.scannedCards}/${progressData.totalCards})`)
                  : (language === "japanese" ? "スキャン進捗" : "Scanning Progress")
                }
              </p>
              <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                <div 
                  className={`h-full transition-all duration-300 ${
                    uploadStatus === 'processing' && progressData && progressData.scannedCards < progressData.totalCards 
                      ? 'bg-blue-500' 
                      : uploadStatus === 'processing' && progressData && progressData.scannedCards === progressData.totalCards
                      ? 'bg-green-500'
                      : 'bg-gray-400'
                  }`}
                  style={{ width: `${getScannedProgress()}%` }}
                />
              </div>
              <p className="text-center mt-1 text-gray-600">{getScannedProgress()}%</p>
            </div>

            {/* Saving Progress */}
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                {progressData 
                  ? (language === "japanese" ? `保存進捗 (${progressData.savedCards}/${progressData.totalCards})` : `Saving Progress (${progressData.savedCards}/${progressData.totalCards})`)
                  : (language === "japanese" ? "保存進捗" : "Saving Progress")
                }
              </p>
              <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                <div 
                  className={`h-full transition-all duration-300 ${
                    uploadStatus === 'processing' && progressData && progressData.scannedCards === progressData.totalCards && progressData.savedCards < progressData.totalCards
                      ? 'bg-blue-500'
                      : uploadStatus === 'processing' && progressData && progressData.savedCards === progressData.totalCards
                      ? 'bg-green-500'
                      : 'bg-gray-400'
                  }`}
                  style={{ width: `${getSavedProgress()}%` }}
                />
              </div>
              <p className="text-center mt-1 text-gray-600">{getSavedProgress()}%</p>
            </div>

            {/* Errors */}
            {progressData && progressData.errors && progressData.errors.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-red-600 mb-2">{language === "japanese" ? "エラー:" : "Errors:"}</p>
                <div className="bg-red-50 border border-red-200 rounded p-2 max-h-32 overflow-y-auto">
                  {progressData.errors.map((error, index) => (
                    <p key={index} className="text-sm text-red-600">{error}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Progress Error */}
            {progressError && (
              <div className="mb-4">
                <p className="text-sm text-orange-600">{progressError}</p>
              </div>
            )}

            <div className="flex justify-center">
              {progressData && progressData.scannedCards === progressData.totalCards && (progressData.savedCards < progressData.totalCards || progressData.status === 'completed') ? (
                <p className="text-gray-600 py-2">
                  {language === "japanese" ? "少々お待ちください..." : "Please wait a bit..."}
                </p>
              ) : (
                <button
                  onClick={handleCancelUpload}
                  className="px-6 py-2 bg-gray-500 text-white rounded-full hover:bg-gray-600"
                >
                  {language === "japanese" ? "キャンセル" : "Cancel"}
                </button>
              )}
            </div>
          </>
        )}
        
        {uploadStatus === 'success' && (
          <>
            <h2 className="text-[#444] text-lg font-bold mb-4 text-center">{language === "japanese" ? "処理完了" : "Processing Complete"}</h2>
            <p className="text-gray-600 mb-4 text-center">
              {language === "japanese" ? "すべての画像の処理が正常に完了しました！" : "All images have been processed successfully!"}
            </p>
            
            {progressData && (
              <div className="mb-4 text-center">
                <p className="text-sm text-gray-600">
                  {language === "japanese" ? `${progressData.totalCards}枚のカードを処理しました` : `Processed ${progressData.totalCards} cards`}
                </p>
                {/* {progressData.completedCardKeys && progressData.completedCardKeys.length > 0 && (
                  <p className="text-sm text-gray-600">
                    {language === "japanese" ? `カードID: ${progressData.completedCardKeys.join(', ')}` : `Card IDs: ${progressData.completedCardKeys.join(', ')}`}
                  </p>
                )} */}
              </div>
            )}
            
            <div className="flex justify-center">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2 text-white rounded-full hover:opacity-80"
                style={{ backgroundColor: 'rgb(255, 106, 12)' }}
              >
                {language === "japanese" ? "OK" : "OK"}
              </button>
            </div>
          </>
        )}
        
        {uploadStatus === 'error' && (
          <>
            <h2 className="text-[#444] text-lg font-bold mb-4 text-center">{language === "japanese" ? "エラー" : "Error"}</h2>
            <p className="text-red-600 mb-4 text-center">{uploadError}</p>
            <div className="flex justify-center">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2 bg-gray-500 text-white rounded-full hover:bg-gray-600"
              >
                {language === "japanese" ? "閉じる" : "Close"}
              </button>
            </div>
          </>
        )}
        
        {uploadStatus === 'cancelled' && (
          <>
            <h2 className="text-[#444] text-lg font-bold mb-4 text-center">{language === "japanese" ? "キャンセル済み" : "Cancelled"}</h2>
            <p className="text-gray-600 mb-4 text-center">
              {language === "japanese" ? "プロセスがキャンセルされました。" : "Process has been cancelled."}
            </p>
            <div className="flex justify-center">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2 bg-gray-500 text-white rounded-full hover:bg-gray-600"
              >
                {language === "japanese" ? "閉じる" : "Close"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <div style={{ borderTop: '1px solid #ddd', marginTop: '1rem', paddingTop: '1rem' }}>
      <div 
        className={`btn btn-primary ${uploadFiles.length < 2 ? 'opacity-50 cursor-not-allowed' : ''}`} 
        style={{ width: "100%" }} 
        onClick={uploadFiles.length >= 2 ? handleStartUpload : undefined}
      >
        {language === "japanese" ? "名刺画像一括インポート" : "Import multiple business card images"}
      </div>

      <div style={{ marginTop: '0.5rem' }}>
        <input
          id="multi-file-input"
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
        />
        {uploadFiles.length > 0 && (
          <p className="text-sm text-gray-600 mt-1">
            {language === "japanese" ? `${uploadFiles.length}個のファイルが選択されています` : `${uploadFiles.length} files selected`}
          </p>
        )}
      </div>

      {uploadModal}
    </div>
  );
};

export default BatchCardUpload;